import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'

export type TemplateBodyPart = { type: 'text'; value: string } | { type: 'variable'; index: number }

// A WhatsApp template body interpolates positional variables as {{1}}, {{2}}, …
// Split it so we can render each variable as a named chip instead of raw tokens.
export function parseTemplateBody(body: string): TemplateBodyPart[] {
    const parts: TemplateBodyPart[] = []
    const pattern = /\{\{(\d+)}}/g
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = pattern.exec(body)) !== null) {
        if (match.index > lastIndex) {
            parts.push({ type: 'text', value: body.slice(lastIndex, match.index) })
        }
        parts.push({ type: 'variable', index: Number(match[1]) })
        lastIndex = match.index + match[0].length
    }

    if (lastIndex < body.length) {
        parts.push({ type: 'text', value: body.slice(lastIndex) })
    }

    return parts
}

function TemplateVariableChip({ index }: { index: number }) {
    const { t } = useTranslation()

    // i18n-extract:
    // t('templateVariables.1')
    // t('templateVariables.2')
    const label = t(`templateVariables.${index}`, { defaultValue: `${index}` })

    return (
        <span className="mx-px inline-block rounded-md bg-primary/10 px-[0.42em] py-[0.16em] text-[0.92em] font-semibold leading-none text-ink-wash ring-1 ring-inset ring-primary/30">
            {label}
        </span>
    )
}

export type TemplateBubbleProps = {
    body: string
    className?: string
    // Fill the row instead of hugging the text, so a list of bubbles shares one edge.
    block?: boolean
}

// The signature of the whole templates surface: a template is shown as the
// message it becomes — an outgoing bubble with its variables highlighted inline.
export function TemplateBubble({ body, className, block }: TemplateBubbleProps) {
    const parts = parseTemplateBody(body)

    return (
        <div
            className={clsx(
                'max-w-full whitespace-pre-wrap rounded-[14px] rounded-bl-[5px] border border-primary/25 bg-primary/[0.08] px-3 py-2 leading-relaxed text-ink',
                block ? 'block' : 'inline-block',
                className
            )}
        >
            {parts.map((part, index) =>
                part.type === 'text' ? (
                    <React.Fragment key={index}>{part.value}</React.Fragment>
                ) : (
                    <TemplateVariableChip
                        key={index}
                        index={part.index}
                    />
                )
            )}
        </div>
    )
}
